<script setup lang="ts">
  import { formatDateLocalized, formatDayOfYear } from '@/utils/dateTime';
  import { StatusStateMap } from '@/components/common/StatusIcon/statusIcon';
  import { computed } from 'vue';
  import CustomerServiceModal from '@/components/common/CustomerServiceModal/CustomerServiceModal.vue';
  import { useI18n } from 'vue-i18n';
  import { customerPage } from '@/cypaths';
  import useCustomerAdmission from '@/composables/useCustomerAdmission';
  import NotificationBox from '@/components/common/NotificationBox/NotificationBox.vue';
  import type { NotificationBoxStatus } from '@/components/common/NotificationBox/notificationBox.ts';

  defineOptions({ name: 'AdmissionValidator' });

  const { t } = useI18n();
  const { admission, expiration } = useCustomerAdmission();

  const messageBoxStatus = computed<Exclude<NotificationBoxStatus, StatusStateMap.Success>>(() => expiration.value?.status === 'expired'
    ? StatusStateMap.Error
    : StatusStateMap.Warning);

  const messageBoxTitle = computed<string>(() => {
    return admission.value && expiration.value
      ? t(`pages.customerProfile.admissions.types.${admission.value.type}.expiration.${expiration.value.status}.title`, {
        date: formatDateLocalized(expiration.value.date),
      })
      : '';
  });
</script>

<template>
  <div
    v-if="admission && expiration?.status"
    class="flex items-center"
  >
    <NotificationBox
      compact
      :data-cy="customerPage.CUSTOMER_STATUS_BOX"
      :status="messageBoxStatus"
      :title="messageBoxTitle"
    >
      <template v-if="admission.type === 'seasonal'">
        <p data-testid="admission-validator-content">
          {{ t(`pages.customerProfile.admissions.types.seasonal.expiration.${expiration.status}.content.text`, {
            endDate: formatDayOfYear(admission.endDateOrTime),
            startDate: formatDayOfYear(admission.startDateOrTime),
          }) }}
          <CustomerServiceModal
            :label="t(`pages.customerProfile.admissions.types.seasonal.expiration.${expiration.status}.content.link.label`)"
            :classes="{ button: 'text-left min-h-auto' }"
          />
        </p>
      </template>
      <template v-else-if="admission.type === 'temporary'">
        <p data-testid="admission-validator-content">
          {{
            t(`pages.customerProfile.admissions.types.temporary.expiration.${expiration.status}.content.text`, {
              date: formatDateLocalized(expiration.date),
            })
          }}
          <CustomerServiceModal
            :label="t(`pages.customerProfile.admissions.types.temporary.expiration.${expiration.status}.content.link.label`)"
            :classes="{ button: 'text-left min-h-auto' }"
          />
        </p>
      </template>
    </NotificationBox>
  </div>
</template>
